import { useEffect, useRef, useState } from "react";
import { AnimatePresence, motion, useReducedMotion } from "framer-motion";
import { useWebsocketStore } from "@/features/tutor/store/useWebsocketStore";
import { FaMicrophone, FaMicrophoneSlash } from "react-icons/fa";
import KeyboardGuide from "@/features/tutor/components/Tutor/KeyboardGuide.jsx";
import { isCachedBiteChoiceActive } from "./cachedBiteChoice";
import {
  CONTROL_BAR_HEIGHT,
  CONTROL_BAR_WIDTH,
} from "@/features/tutor/constants/controlBar";

// Bar geometry
const EQ_BAR_H = 13;
const EQ_BAR_W = 3; // must match SVG width attribute
const EQ_MIN_H = EQ_BAR_W; // circle at minimum: height = width
const EQ_INIT_Y = (EQ_BAR_H - EQ_MIN_H) / 2; // center the bar vertically
const EQ_MIN_SCALE = EQ_MIN_H / EQ_BAR_H; // ≈ 0.38

// Module-level state — survives EqBars remounts (user releasing/pressing button).
// Assumes a single EqBars instance at a time; multiple simultaneous instances would share this state.
const eqGains = [2, 5, 5];
const eqSmoothed = [EQ_MIN_SCALE, EQ_MIN_SCALE, EQ_MIN_SCALE];
let eqFreqBuf = null; // allocated once when analyser first available
let eqRanges = null; // [[start,end], ...] computed once from frequencyBinCount

function initEqStatics(analyser) {
  if (eqFreqBuf && eqRanges) return;
  const n = analyser.frequencyBinCount;
  eqFreqBuf = new Uint8Array(n);
  const voiceEnd = Math.floor(n * 0.35);
  const b1 = Math.max(1, Math.floor(voiceEnd * 0.25));
  const b2 = Math.max(b1 + 1, Math.floor(voiceEnd * 0.6));
  eqRanges = [
    [1, b1],
    [b1, b2],
    [b2, voiceEnd],
  ];
}

function EqBars() {
  const ref1 = useRef(null);
  const ref2 = useRef(null);
  const ref3 = useRef(null);
  const rafRef = useRef(null);

  useEffect(() => {
    eqSmoothed[0] = EQ_MIN_SCALE;
    eqSmoothed[1] = EQ_MIN_SCALE;
    eqSmoothed[2] = EQ_MIN_SCALE;
    const refs = [ref1, ref2, ref3];
    const loop = () => {
      const analyser = window.__micAnalyserNode;
      if (analyser && ref1.current && ref2.current && ref3.current) {
        initEqStatics(analyser);
        analyser.getByteFrequencyData(eqFreqBuf);

        eqRanges.forEach(([start, end], i) => {
          let s = 0;
          for (let j = start; j < end; j++) s += eqFreqBuf[j];
          const raw = s / ((end - start) * 255);
          const level = Math.max(EQ_MIN_SCALE, Math.min(1.0, raw * eqGains[i]));

          if (level >= 0.95) {
            eqGains[i] = Math.max(1, eqGains[i] * 0.993);
          } else if (level <= EQ_MIN_SCALE + 0.01 && raw > 0.002) {
            eqGains[i] = Math.min(100, eqGains[i] * 1.003);
          }

          // Fast attack (α=0.15), slightly slower decay (α=0.35)
          const alpha = level > eqSmoothed[i] ? 0.15 : 0.35;
          eqSmoothed[i] = eqSmoothed[i] * alpha + level * (1 - alpha);

          const visH = Math.max(EQ_MIN_H, EQ_BAR_H * eqSmoothed[i]);
          refs[i].current.setAttribute("height", visH);
          refs[i].current.setAttribute("y", (EQ_BAR_H - visH) / 2);
        });
      }
      rafRef.current = requestAnimationFrame(loop);
    };
    rafRef.current = requestAnimationFrame(loop);
    return () => {
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
    };
  }, []);

  return (
    <svg
      width="19"
      height="13"
      viewBox="0 0 19 13"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      style={{ overflow: "visible", pointerEvents: "none" }}
    >
      <rect
        ref={ref1}
        x="0"
        y={EQ_INIT_Y}
        width={EQ_BAR_W}
        height={EQ_MIN_H}
        rx={EQ_BAR_W / 2}
        fill="#1C9C6D"
      />
      <rect
        ref={ref2}
        x="8"
        y={EQ_INIT_Y}
        width={EQ_BAR_W}
        height={EQ_MIN_H}
        rx={EQ_BAR_W / 2}
        fill="#1C9C6D"
      />
      <rect
        ref={ref3}
        x="16"
        y={EQ_INIT_Y}
        width={EQ_BAR_W}
        height={EQ_MIN_H}
        rx={EQ_BAR_W / 2}
        fill="#1C9C6D"
      />
    </svg>
  );
}

export default function UnifiedActionButton({
  isPressingToTalk,
  isAwaitingTutorResponse,
  hasPendingWork,
  micUnavailable,
  hideWarningIcon,
  showKeyboardGuide,
  compact = false,
  stretched = false,
  onPointerDown,
  onPointerUp,
  cachedBitePromptState = undefined,
  onCachedBiteChoice = undefined,
  onCachedBiteAttention = undefined,
  cachedBiteChoiceReady = true,
  isWsConnected,
}) {
  const wsConnectedStore = useWebsocketStore((state) => state.wsConnected);
  // Callers not tied to the tutor WS store (e.g. Spaces) can drive connection
  // state directly via the isWsConnected prop; otherwise fall back to the store.
  const wsConnected = isWsConnected ?? wsConnectedStore;
  const continueChoiceEnabled = cachedBiteChoiceReady && wsConnected;
  const [focusedChoice, setFocusedChoice] = useState("continue");
  const restartChoiceRef = useRef(null);
  const continueChoiceRef = useRef(null);
  const reduceMotion = useReducedMotion();
  const promptAnnouncing = cachedBitePromptState?.status === "announcing";
  const choiceActive = isCachedBiteChoiceActive(cachedBitePromptState);
  const cachedPromptVisualActive = promptAnnouncing || choiceActive;
  const isApplying = cachedBitePromptState?.status === "applying";
  const applyingChoice = isApplying ? cachedBitePromptState.choice : null;
  const attentionNonce = cachedBitePromptState?.attentionNonce || 0;

  useEffect(() => {
    if (choiceActive) {
      setFocusedChoice(continueChoiceEnabled ? "continue" : "restart");
    }
  }, [choiceActive, cachedBitePromptState?.slideIndex, continueChoiceEnabled]);

  useEffect(() => {
    if (!choiceActive) return;
    const target =
      focusedChoice === "restart"
        ? restartChoiceRef.current
        : continueChoiceRef.current;
    target?.focus?.();
  }, [choiceActive, focusedChoice]);

  useEffect(() => {
    if (!choiceActive) return undefined;
    const handleChoiceKeyDown = (event) => {
      if (event.code === "ArrowLeft" || event.code === "ArrowRight") {
        event.preventDefault();
        setFocusedChoice((choice) =>
          choice === "restart" ? "continue" : "restart",
        );
        return;
      }
      if (event.code === "Enter") {
        event.preventDefault();
        if (
          !isApplying &&
          (focusedChoice === "restart" || continueChoiceEnabled)
        ) {
          onCachedBiteChoice?.(focusedChoice);
        } else if (focusedChoice === "continue") {
          onCachedBiteAttention?.();
        }
        return;
      }
      if (event.code === "Escape") {
        event.preventDefault();
        onCachedBiteAttention?.();
      }
    };
    window.addEventListener("keydown", handleChoiceKeyDown);
    return () => window.removeEventListener("keydown", handleChoiceKeyDown);
  }, [
    choiceActive,
    focusedChoice,
    isApplying,
    onCachedBiteAttention,
    onCachedBiteChoice,
    continueChoiceEnabled,
  ]);

  if (cachedPromptVisualActive) {
    const wrapperClass = compact
      ? `flex items-center justify-center gap-2 ${stretched ? "w-full h-full min-w-[112px] min-h-[48px]" : "w-[112px] h-[48px]"}`
      : `flex items-center justify-center gap-3 ${CONTROL_BAR_WIDTH} ${CONTROL_BAR_HEIGHT}`;
    const attentionAnimation = attentionNonce
      ? reduceMotion
        ? { scale: [1, 1.02, 1] }
        : { x: [0, -6, 6, -4, 4, 0] }
      : { x: 0, scale: 1 };
    const attentionTransition = attentionNonce
      ? { duration: 0.22, ease: "easeInOut" }
      : { duration: 0.18, ease: "easeOut" };
    const mainTransition = { type: "spring", stiffness: 380, damping: 30 };
    const noButtonTransition = {
      type: "spring",
      stiffness: 360,
      damping: 28,
      delay: 0.1,
    };
    const compactChoiceWidth = stretched ? "calc((100% - 8px) / 2)" : 52;
    const promptMainWidth = compact
      ? promptAnnouncing || isApplying
        ? "100%"
        : compactChoiceWidth
      : promptAnnouncing
        ? 300
        : isApplying
          ? 252
          : 138;
    const continueChoiceWidth = compact ? compactChoiceWidth : 138;
    const loadingLabel =
      applyingChoice === "restart"
        ? "Restarting the bite"
        : "Resuming the bite";
    const loadingDisplayLabel = compact
      ? applyingChoice === "restart"
        ? "Restarting"
        : "Resuming"
      : loadingLabel;

    return (
      <div className={wrapperClass}>
        <motion.button
          ref={restartChoiceRef}
          type="button"
          aria-label={choiceActive ? "Yes, restart this bite" : "Hold to speak"}
          disabled={promptAnnouncing || isApplying}
          initial={false}
          animate={{
            width: promptMainWidth,
            backgroundColor: isApplying ? "#1c1c2e" : "#ffffff",
            opacity: 1,
            scale: 1,
            ...(choiceActive ? attentionAnimation : {}),
          }}
          transition={
            choiceActive && attentionNonce
              ? attentionTransition
              : mainTransition
          }
          onClick={() => {
            if (choiceActive && !isApplying) {
              onCachedBiteChoice?.("restart");
            }
          }}
          onFocus={() => {
            if (choiceActive) {
              setFocusedChoice("restart");
            }
          }}
          whileHover={!isApplying && !promptAnnouncing ? { scale: 1.03 } : {}}
          whileTap={!isApplying && !promptAnnouncing ? { scale: 0.96 } : {}}
          className={`prevent-select flex h-full min-h-[44px] shrink-0 items-center justify-center overflow-hidden rounded-full px-3 font-semibold focus:outline-none focus:ring-2 focus:ring-white/90 disabled:cursor-default disabled:opacity-100 ${compact ? "text-xs" : "text-[15px]"}`}
          style={{
            border: isApplying
              ? "1px solid rgba(255,255,255,0.1)"
              : "2px solid #e0e0e0",
            boxShadow: isApplying
              ? "0 4px 24px rgba(0,0,0,0.35)"
              : "0 4px 20px rgba(0,0,0,0.12)",
          }}
        >
          <AnimatePresence mode="wait" initial={false}>
            {promptAnnouncing && (
              <motion.span
                key="speak-content"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                transition={{ duration: 0.14 }}
                className="flex items-center gap-3 whitespace-nowrap text-[#252525]"
              >
                <FaMicrophone className="h-4 w-4 shrink-0" />
                {!compact && <span>Hold to Speak</span>}
              </motion.span>
            )}
            {choiceActive && !isApplying && (
              <motion.span
                key="yes-content"
                initial={{ opacity: 0, scale: 0.8 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={{ opacity: 0, scale: 0.8 }}
                transition={{ duration: 0.14 }}
                className="whitespace-nowrap text-[#252525]"
              >
                Yes
              </motion.span>
            )}
            {isApplying && (
              <motion.span
                key="loading-content"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                transition={{ duration: 0.14 }}
                className="flex items-center gap-3 whitespace-nowrap px-3 text-white"
              >
                <span className="h-4 w-4 shrink-0 animate-spin rounded-full border-2 border-white/20 border-t-white" />
                <span className="truncate text-sm font-bold">
                  {loadingDisplayLabel}
                </span>
              </motion.span>
            )}
          </AnimatePresence>
        </motion.button>
        <AnimatePresence initial={false}>
          {choiceActive && !isApplying && (
            <motion.button
              key="no-btn"
              ref={continueChoiceRef}
              type="button"
              aria-label="No, continue this bite"
              initial={{ width: 0, opacity: 0, scale: 0.75 }}
              animate={{
                width: continueChoiceWidth,
                opacity: 1,
                scale: 1,
                ...attentionAnimation,
              }}
              exit={{ width: 0, opacity: 0, scale: 0.75 }}
              transition={
                attentionNonce ? attentionTransition : noButtonTransition
              }
              disabled={!continueChoiceEnabled}
              onClick={() => {
                if (!continueChoiceEnabled) {
                  onCachedBiteAttention?.();
                  return;
                }
                onCachedBiteChoice?.("continue");
              }}
              onFocus={() => setFocusedChoice("continue")}
              whileHover={continueChoiceEnabled ? { scale: 1.04 } : undefined}
              whileTap={continueChoiceEnabled ? { scale: 0.95 } : undefined}
              className={`prevent-select flex h-full min-h-[44px] shrink-0 items-center justify-center overflow-hidden rounded-full bg-white px-3 font-semibold text-[#252525] focus:outline-none focus:ring-2 focus:ring-white/90 ${compact ? "text-xs" : "text-[15px]"}`}
              style={{
                border: "2px solid #e0e0e0",
                boxShadow: "0 4px 20px rgba(0,0,0,0.12)",
              }}
            >
              <motion.span
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                transition={{ delay: 0.2, duration: 0.1 }}
                className="truncate"
              >
                No
              </motion.span>
            </motion.button>
          )}
        </AnimatePresence>
      </div>
    );
  }

  if (compact) {
    return (
      <div
        className={`flex flex-col items-center relative ${stretched ? "w-full h-full min-w-[48px] min-h-[48px]" : ""}`}
      >
        <button
          className={`prevent-select flex items-center justify-center transition-colors rounded-full
          ${stretched ? "w-full h-full min-w-[48px] min-h-[48px]" : "w-[48px] h-[48px]"}
          ${showKeyboardGuide && !micUnavailable ? "animate-pulse" : ""}
          ${
            isPressingToTalk
              ? "bg-[#373737] text-white"
              : isAwaitingTutorResponse
                ? "bg-[#373737] text-white cursor-not-allowed"
                : !wsConnected
                  ? "bg-white/30 text-white/40 cursor-not-allowed"
                  : hasPendingWork
                    ? "bg-[#1C9C6D] text-white"
                    : micUnavailable
                      ? "bg-white/30 text-white/40 cursor-not-allowed"
                      : "bg-white text-black"
          }`}
          disabled={isAwaitingTutorResponse || !wsConnected}
          onPointerDown={(e) => {
            e.currentTarget.setPointerCapture(e.pointerId);
            onPointerDown?.(e);
          }}
          onPointerUp={onPointerUp}
          onPointerCancel={onPointerUp}
          onLostPointerCapture={onPointerUp}
          onContextMenu={(e) => e.preventDefault()}
          style={{ touchAction: "none" }}
        >
          {isPressingToTalk ? (
            <EqBars />
          ) : isAwaitingTutorResponse ? (
            <svg
              width="14"
              height="14"
              viewBox="0 0 14 14"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
              style={{ pointerEvents: "none" }}
            >
              <circle cx="7" cy="7" r="5" fill="white" />
              <path
                d="M7 0C5.61553 0 4.26215 0.410543 3.11101 1.17971C1.95987 1.94888 1.06266 3.04213 0.532846 4.32121C0.00303299 5.6003 -0.13559 7.00776 0.134506 8.36563C0.404603 9.7235 1.07129 10.9708 2.05026 11.9497C3.02922 12.9287 4.2765 13.5954 5.63437 13.8655C6.99224 14.1356 8.3997 13.997 9.67878 13.4672C10.9579 12.9373 12.0511 12.0401 12.8203 10.889C13.5895 9.73784 14 8.38447 14 7C14 6.08075 13.8189 5.17049 13.4672 4.32121C13.1154 3.47194 12.5998 2.70026 11.9497 2.05025C11.2997 1.40024 10.5281 0.884626 9.67878 0.532843C8.8295 0.18106 7.91925 0 7 0ZM10.297 5.397L6.097 9.597C6.03193 9.66261 5.95451 9.71468 5.8692 9.75022C5.7839 9.78576 5.69241 9.80406 5.6 9.80406C5.50759 9.80406 5.4161 9.78576 5.3308 9.75022C5.2455 9.71468 5.16808 9.66261 5.103 9.597L3.703 8.197C3.63773 8.13173 3.58596 8.05425 3.55064 7.96897C3.51532 7.8837 3.49714 7.7923 3.49714 7.7C3.49714 7.6077 3.51532 7.5163 3.55064 7.43102C3.58596 7.34575 3.63773 7.26826 3.703 7.203C3.76827 7.13773 3.84575 7.08596 3.93103 7.05064C4.0163 7.01531 4.1077 6.99713 4.2 6.99713C4.2923 6.99713 4.3837 7.01531 4.46898 7.05064C4.55425 7.08596 4.63173 7.13773 4.697 7.203L5.6 8.113L9.303 4.403C9.43481 4.27119 9.61359 4.19713 9.8 4.19713C9.98641 4.19713 10.1652 4.27119 10.297 4.403C10.4288 4.53481 10.5029 4.71359 10.5029 4.9C10.5029 5.08641 10.4288 5.26519 10.297 5.397Z"
                fill="#1C9C6D"
              />
            </svg>
          ) : hasPendingWork ? (
            <svg
              width="17"
              height="17"
              viewBox="0 0 17 17"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
              style={{ pointerEvents: "none" }}
            >
              <path
                fillRule="evenodd"
                clipRule="evenodd"
                d="M12.1611 4.83893L6.55102 8.73395L0.683184 6.77774C0.273599 6.64093 -0.00234163 6.25665 1.49778e-05 5.82493C0.0024026 5.3932 0.281506 5.01128 0.692672 4.87924L15.6947 0.0480388C16.0513 -0.0665978 16.4427 0.0274805 16.7076 0.292382C16.9725 0.557284 17.0666 0.948667 16.9519 1.30529L12.1207 16.3073C11.9887 16.7185 11.6068 16.9976 11.1751 17C10.7433 17.0023 10.3591 16.7264 10.2222 16.3168L8.25655 10.4205L12.1611 4.83893Z"
                fill="white"
              />
            </svg>
          ) : (
            <FaMicrophoneSlash className="w-5 h-5 pointer-events-none" />
          )}
          {micUnavailable && !hideWarningIcon && (
            <img
              src="/assets/warning_alt.svg"
              alt="Microphone disabled"
              className="absolute -top-1 -right-1 w-5 h-5 pointer-events-none"
            />
          )}
        </button>
      </div>
    );
  }

  return (
    <div className="flex flex-col items-center relative">
      <button
        className={`prevent-select ${CONTROL_BAR_HEIGHT} rounded-2xl flex items-center justify-center gap-3 transition-colors ${CONTROL_BAR_WIDTH}
        ${showKeyboardGuide && !micUnavailable ? "animate-pulse" : ""}
        ${
          isPressingToTalk
            ? "bg-[#373737] text-white hover:bg-[#373737]"
            : isAwaitingTutorResponse
              ? "bg-[#373737] text-white cursor-not-allowed"
              : !wsConnected
                ? "bg-white/30 text-white/40 cursor-not-allowed"
                : hasPendingWork
                  ? "bg-[#1C9C6D] text-white"
                  : micUnavailable
                    ? "bg-white/30 text-white/40 cursor-not-allowed"
                    : "bg-white text-black"
        }`}
        disabled={isAwaitingTutorResponse || !wsConnected}
        onPointerDown={(e) => {
          e.currentTarget.setPointerCapture(e.pointerId);
          onPointerDown?.(e);
        }}
        onPointerUp={onPointerUp}
        onPointerCancel={onPointerUp}
        onLostPointerCapture={onPointerUp}
        onContextMenu={(e) => e.preventDefault()}
        style={{ touchAction: "none" }}
      >
        {isPressingToTalk ? (
          <EqBars />
        ) : isAwaitingTutorResponse ? (
          <svg
            width="14"
            height="14"
            viewBox="0 0 14 14"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
            style={{ pointerEvents: "none" }}
          >
            <circle cx="7" cy="7" r="5" fill="white" />
            <path
              d="M7 0C5.61553 0 4.26215 0.410543 3.11101 1.17971C1.95987 1.94888 1.06266 3.04213 0.532846 4.32121C0.00303299 5.6003 -0.13559 7.00776 0.134506 8.36563C0.404603 9.7235 1.07129 10.9708 2.05026 11.9497C3.02922 12.9287 4.2765 13.5954 5.63437 13.8655C6.99224 14.1356 8.3997 13.997 9.67878 13.4672C10.9579 12.9373 12.0511 12.0401 12.8203 10.889C13.5895 9.73784 14 8.38447 14 7C14 6.08075 13.8189 5.17049 13.4672 4.32121C13.1154 3.47194 12.5998 2.70026 11.9497 2.05025C11.2997 1.40024 10.5281 0.884626 9.67878 0.532843C8.8295 0.18106 7.91925 0 7 0ZM10.297 5.397L6.097 9.597C6.03193 9.66261 5.95451 9.71468 5.8692 9.75022C5.7839 9.78576 5.69241 9.80406 5.6 9.80406C5.50759 9.80406 5.4161 9.78576 5.3308 9.75022C5.2455 9.71468 5.16808 9.66261 5.103 9.597L3.703 8.197C3.63773 8.13173 3.58596 8.05425 3.55064 7.96897C3.51532 7.8837 3.49714 7.7923 3.49714 7.7C3.49714 7.6077 3.51532 7.5163 3.55064 7.43102C3.58596 7.34575 3.63773 7.26826 3.703 7.203C3.76827 7.13773 3.84575 7.08596 3.93103 7.05064C4.0163 7.01531 4.1077 6.99713 4.2 6.99713C4.2923 6.99713 4.3837 7.01531 4.46898 7.05064C4.55425 7.08596 4.63173 7.13773 4.697 7.203L5.6 8.113L9.303 4.403C9.43481 4.27119 9.61359 4.19713 9.8 4.19713C9.98641 4.19713 10.1652 4.27119 10.297 4.403C10.4288 4.53481 10.5029 4.71359 10.5029 4.9C10.5029 5.08641 10.4288 5.26519 10.297 5.397Z"
              fill="#1C9C6D"
            />
          </svg>
        ) : hasPendingWork ? (
          <svg
            width="17"
            height="17"
            viewBox="0 0 17 17"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
            style={{ pointerEvents: "none" }}
          >
            <path
              fillRule="evenodd"
              clipRule="evenodd"
              d="M12.1611 4.83893L6.55102 8.73395L0.683184 6.77774C0.273599 6.64093 -0.00234163 6.25665 1.49778e-05 5.82493C0.0024026 5.3932 0.281506 5.01128 0.692672 4.87924L15.6947 0.0480388C16.0513 -0.0665978 16.4427 0.0274805 16.7076 0.292382C16.9725 0.557284 17.0666 0.948667 16.9519 1.30529L12.1207 16.3073C11.9887 16.7185 11.6068 16.9976 11.1751 17C10.7433 17.0023 10.3591 16.7264 10.2222 16.3168L8.25655 10.4205L12.1611 4.83893Z"
              fill="white"
            />
          </svg>
        ) : (
          <FaMicrophoneSlash className="w-5 h-5 pointer-events-none" />
        )}
        {hasPendingWork &&
        !isPressingToTalk &&
        !isAwaitingTutorResponse &&
        wsConnected ? (
          <span className="flex flex-col items-center leading-[1.1] prevent-select">
            <span className="text-base font-medium">Tap to Submit</span>
            <span className="text-xs font-light opacity-80">
              Hold to discuss
            </span>
          </span>
        ) : (
          <span className="text-base font-medium prevent-select">
            {isPressingToTalk
              ? "Listening..."
              : !wsConnected
                ? "Reconnecting..."
                : isAwaitingTutorResponse
                  ? "Submitted"
                  : "Hold to speak"}
          </span>
        )}
      </button>
      {micUnavailable && !hideWarningIcon && (
        <img
          src="/assets/warning_alt.svg"
          alt="Microphone disabled"
          className="absolute -top-1 -right-1 w-5 h-5 pointer-events-none"
        />
      )}
      <KeyboardGuide showKeyboardGuide={showKeyboardGuide} />
    </div>
  );
}
